// src/components/Story/StoryReader.js
import React, { useEffect, useState, useMemo } from "react";
import PaywallModal from "../modals/PaywallModal";
import { useUserData } from "../../context/UserDataContext";
import { useParams, useNavigate } from "react-router-dom";
import { listSavedFlashcardIds, saveFlashcard, removeFlashcard } from "../../data/flashcardsRepo";
import { ensureStoryProgressOnOpen, markStoryFinished, markStoryInProgress } from "../../data/storyRepo";
import { getDictionaryEntryWithFallback } from "../../data/sidebarRepo";
import { useAuth } from "../../context/AuthContext";
import StorySidebar from "./StorySidebar";
import StoryContent from "./StoryContent";
import StoryQuiz from "./StoryQuiz";
import StoryKeywords from "./StoryKeywords";
import { fetchStoryAssets } from "../../data/storyRepo";
import LoadingScreen from "../modals/LoadingScreen";

import { logEvent } from "firebase/analytics";
import { analytics } from "../../firebase";

import { useFavoriteStory } from "../../hooks/useFavoriteStory";
import "./StoryReader.css";

import { GoBook } from "react-icons/go";
import { LuBrain } from "react-icons/lu";
import { GrHelpBook } from "react-icons/gr";
import { IoMdHelpCircleOutline } from "react-icons/io";
import { IoIosArrowBack } from "react-icons/io";
import { IoIosArrowForward } from "react-icons/io";
import { FaHeart, FaRegHeart } from "react-icons/fa";
import { MdOutlineQuiz } from "react-icons/md";
import { RiBookMarkedLine } from "react-icons/ri";
import { FaRegCheckCircle, FaCheckCircle } from "react-icons/fa";
import { MdCheckCircleOutline } from "react-icons/md";
import { CiCircleCheck } from "react-icons/ci";
import { FaArrowLeft } from "react-icons/fa";

const cache = {};

export default function StoryReader() {
  const { id } = useParams();
  const { userData } = useUserData();
  const [story, setStory] = useState(null);
  const [tokens, setTokens] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedWord, setSelectedWord] = useState(null);
  const [anchorRect, setAnchorRect] = useState(null);
  const [wordData, setWordData] = useState(null);
  const [loadingWord, setLoadingWord] = useState(false);
  const [showTranslations, setShowTranslations] = useState(false);
  const [activeTab, setActiveTab] = useState("story");
  const [addedWords, setAddedWords] = useState([]);
  const [isFinished, setIsFinished] = useState(false);
  const [savedWords, setSavedWords] = useState([]);
  const [showPaywall, setShowPaywall] = useState(false);
  const [keywordReviewActive, setKeywordReviewActive] = useState(true);
  const [keywordReviewCards, setKeywordReviewCards] = useState([]);
  const [sectionIndex, setSectionIndex] = useState(0);
  const [quizUrl, setQuizUrl] = useState(null);

  const { user } = useAuth();
  const navigate = useNavigate();
  //const [isFavorite, setIsFavorite] = useState(false);
  const [isFavorite, toggleFavorite] = useFavoriteStory(story, user);

  

  const language = "de";   // temporary, until you implement language switching

  const isPaidUser = userData?.hasAccess && userData?.subscriptionStatus === "active";

  const isMobile = window.matchMedia("(max-width: 900px)").matches;

  const storySections = useMemo(() => {
    if (!story?.sentences?.length) return [];
  
    // group sentences by paragraph number
    const byParagraph = story.sentences.reduce((acc, s) => {
      const p = s.paragraph ?? 1;
      (acc[p] ||= []).push(s);
      return acc;
    }, {});
  
    const paragraphNums = Object.keys(byParagraph)
      .map(Number)
      .sort((a, b) => a - b);
  
    // ---- ADAPTIVE SETTINGS ----
    const targetChars = 800;     // tweak this (700–1000 usually good)
    const minParagraphs = 2;
    const maxParagraphs = 14;
    // ----------------------------
  
    const sections = [];
    let currentParagraphs = [];
    let currentSentences = [];
    let currentSize = 0;
  
    for (const pNum of paragraphNums) {
      const sentences = byParagraph[pNum];
      const paragraphSize = sentences.reduce(
        (sum, s) => sum + (s.text?.length ?? 0),
        0
      );
  
      const wouldExceed = currentSize + paragraphSize > targetChars;
      const hitMax = currentParagraphs.length >= maxParagraphs;
  
      if (
        currentParagraphs.length > 0 &&
        (hitMax || (wouldExceed && currentParagraphs.length >= minParagraphs))
      ) {
        sections.push({
          paragraphNums: currentParagraphs,
          sentences: currentSentences,
        });
  
        currentParagraphs = [];
        currentSentences = [];
        currentSize = 0;
      }
  
      currentParagraphs.push(pNum);
      currentSentences.push(...sentences);
      currentSize += paragraphSize;
    }
  
    if (currentParagraphs.length) {
      sections.push({
        paragraphNums: currentParagraphs,
        sentences: currentSentences,
      });
    }
  
    return sections;
  }, [story]);

    // keep index in range if story reloads / changes
  useEffect(() => {
    if (!storySections.length) return;
    setSectionIndex((prev) => Math.min(prev, storySections.length - 1));
  }, [storySections.length]);

  const handleNext = () => {
    if (!storySections?.length) return;
  
    const last = storySections.length - 1;
  
    if (sectionIndex < last) {
      setSectionIndex((i) => i + 1);
  
      // optional: jump to top so user doesn't stay scrolled down
      requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "smooth" }));
    } else {
      setActiveTab("keywords");
    }
  };

  const handleBack = () => {
    if (sectionIndex > 0) {
      setSectionIndex((i) => i - 1);
  
      requestAnimationFrame(() =>
        window.scrollTo({ top: 0, behavior: "smooth" })
      );
    }
  };


  const floatingStyle =
    isMobile && anchorRect
      ? (() => {
          const GAP = 28
          const w = Math.min(360, window.innerWidth - 24);
          const left = Math.max(
            12,
            Math.min(anchorRect.left + anchorRect.width / 2 - w / 2, window.innerWidth - w - 12)
          );
          const top = Math.max(12, anchorRect.top - GAP); // will shift above via CSS transform
          return { width: w, left, top };
        })()
      : undefined;

  // Fetch user's saved flashcards
  useEffect(() => {
    if (!user?.uid) return;
  
    const fetchSavedWords = async () => {
      try {
        const ids = await listSavedFlashcardIds({ uid: user.uid, language });
        setSavedWords(ids);
      } catch (err) {
        console.error("Error fetching saved words:", err);
      }
    };
  
    fetchSavedWords();
  }, [user?.uid, language]);
  
  

  // Fetch story + tokens
  useEffect(() => {
    const fetchData = async () => {
      try {

        // const folder = `${id}_${language}`;

        // const [storyRes, tokenRes] = await Promise.all([
        // fetch(`/stories/${folder}/story.json`),
        // fetch(`/stories/${folder}/tokens.json`),
        // ]);

        // if (!storyRes.ok) throw new Error("Story not found");

        const { storyData, tokensData, quizUrl, access } = await fetchStoryAssets(id, language);
        // const storyData = await storyRes.json();
        // const tokensData = tokenRes.ok ? await tokenRes.json() : null;

        const canAccess = isPaidUser || access === "free";

        if (!canAccess) {
          setShowPaywall(true);
          setStory(null);     // block rendering the rest
          return;
        }

        setStory(storyData);
        setTokens(tokensData);
        setQuizUrl(quizUrl);
      } catch (err) {
        console.error("Error loading story:", err?.message, err?.code, err);
        setStory(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    return () => Object.keys(cache).forEach((k) => delete cache[k]);
  }, [id, language, isPaidUser]);


  useEffect(() => {
    if (!user?.uid || !story?.id) return;
  
    const run = async () => {
      try {
        const existing = await ensureStoryProgressOnOpen({
          uid: user.uid,
          language,
          storyId: story.id,
          title: story.title,
        });
  
        // set finished state based on existing doc if it existed
        if (existing) setIsFinished(existing.status === "finished");
        else setIsFinished(false);
      } catch (err) {
        console.error("Error ensuring story progress:", err);
      }
    };
  
    run();
  }, [user?.uid, story?.id, language]);

  useEffect(() => {
    if (activeTab !== "story") {
      setSelectedWord(null);
      setWordData(null);
      setAnchorRect(null);
      setLoadingWord(false);
    }
  }, [activeTab]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  

  const handleFinishClick = async () => {
    if (!user?.uid || !story?.id) return;
  
    setIsFinished((prev) => !prev); // optimistic
  
    try {
      if (isFinished) {
        await markStoryInProgress({ uid: user.uid, language, storyId: story.id });
      } else {
        await markStoryFinished({ uid: user.uid, language, storyId: story.id });
  
        const audio = new Audio("/sounds/finishstory.mp3");
        audio.volume = 0.6;
        audio.play().catch(() => {});
      }

      if (analytics) {
        logEvent(analytics, "story_finished", {
          story_id: story.id
        });
      }
    } catch (err) {
      console.error("Error updating progress status:", err);
      setIsFinished((prev) => !prev); // revert optimistic
    }
  };
  
  

  const handleBackClick = () => {
    // if user has history, go back (enables iOS swipe expectation)
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      // deep link / opened fresh -> no history, so go home
      navigate("/home", { replace: true });
    }
  };

  // Flashcards
  async function handleAddFlashcard(wordData, sentence) {
    if (!user?.uid || !wordData) return;
  
    const wordKey = wordData.word.toLowerCase();
    const isSaved = savedWords.includes(wordKey);
  
    // optimistic UI
    if (isSaved) {
      setSavedWords((prev) => prev.filter((w) => w !== wordKey));
    } else {
      setSavedWords((prev) => [...new Set([...prev, wordKey])]);
    }
  
    try {
      if (isSaved) {
        await removeFlashcard({ uid: user.uid, language, wordKey });
      } else {
        await saveFlashcard({
          uid: user.uid,
          language,
          wordKey,
          wordData,
          storyId: story.id,
          sentence: sentence.text || null,
          sentenceTranslation: sentence.translation || null,
        });
  
        const audio = new Audio("/sounds/saveword.mp3");
        audio.volume = 0.5;
        audio.play().catch(() => {});

        if (analytics) {
          logEvent(analytics, "flashcard_saved", {
            story_id: story.id
          });
        }
      }
    } catch (err) {
      console.error("Error toggling flashcard:", err);
  
      // revert optimistic change
      setSavedWords((prev) =>
        isSaved
          ? [...new Set([...prev, wordKey])]
          : prev.filter((w) => w !== wordKey)
      );
    }
  }
  


  
  

  async function handleWordClick(wordObj, e) {
    if (!wordObj?.lemma || !wordObj.pos) return;

      // NEW: store the clicked word position (for mobile popover)
    if (e?.currentTarget) {
      setAnchorRect(e.currentTarget.getBoundingClientRect());
    } else {
      setAnchorRect(null);
    }

    console.log("rect", e.currentTarget.getBoundingClientRect());
    console.log("tag", e.currentTarget.tagName, e.currentTarget.className);


    const { word, lemma, pos } = wordObj;

    if (selectedWord === word) {
      setSelectedWord(null);
      setWordData(null);
      setAnchorRect(null); // NEW: clear
      return;
    }

    setSelectedWord(word);
    setLoadingWord(true);
    setWordData(null);

    const cacheKey = `${lemma}_${pos}`;
    if (cache[cacheKey]) {
      setWordData(cache[cacheKey]);
      setLoadingWord(false);
      return;
    }

    try {
      const entry = await getDictionaryEntryWithFallback({ lemma, pos });
      const result = entry || { notFound: true };
      cache[cacheKey] = result;
      setWordData(result);
    } catch {
      setWordData({ notFound: true });
    } finally {
      setLoadingWord(false);
    }
    
  }

  if (showPaywall) {
    return <PaywallModal onClose={() => navigate("/")} />;
  }

  if (loading) return <LoadingScreen debugLabel="story-reader" />;
  if (!story) return <p>Story not found.</p>;

  return (
<div className="story-page-wrapper">
  <div className="story-layout">
    <div className="story-content">
      <div className="story-content-card">

        <div className="story-header-info">

          {/* Back row */}
          <div className="story-back-row" onClick={handleBackClick}>
            <FaArrowLeft size={14} />
            <span>Voltar</span>
          </div>

          {/* Title + badge + heart */}
          <div className="story-title-row">
            <h1 className="story-title-text">{story.title}</h1>
            <div className="story-header-actions">
              <span className={`story-level-badge level-${story.level.toLowerCase()}`}>{story.level}</span>
              <div className="story-finish-btn tooltip-container">
                {isFavorite ? (
                  <>
                    <FaHeart size={22} className="finish-icon filled" onClick={toggleFavorite} color="var(--color-primary)" />
                    <span className="tooltip-text">Remover dos favoritos</span>
                  </>
                ) : (
                  <>
                    <FaRegHeart size={22} className="finish-icon" onClick={toggleFavorite} />
                    <span className="tooltip-text">Adicionar aos favoritos</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="story-tabs">
            <button className={`story-tab ${activeTab === "story" ? "active" : ""}`} onClick={() => setActiveTab("story")}>
              <GoBook /> 
              História
            </button>
            <button className={`story-tab ${activeTab === "keywords" ? "active" : ""}`} onClick={() => setActiveTab("keywords")}>
              <LuBrain /> 
              Flashcards
            </button>
            <button className={`story-tab ${activeTab === "quiz" ? "active" : ""}`} onClick={() => setActiveTab("quiz")}>
              <GrHelpBook /> 
              Quiz
            </button>
          </div>

        </div>
          
          <div className="story-divider-full" /> 

            <div className="story-tab-panel">
            {activeTab === "story" && (
              <div className="story-tab-content">
                <StoryContent
                  story={story}
                  tokens={tokens}
                  sectionSentences={storySections[sectionIndex]?.sentences || []}
                  sectionIndex={sectionIndex}
                  totalSections={storySections.length}
                  showTranslations={showTranslations}
                  onToggleTranslations={() => setShowTranslations((p) => !p)}
                  selectedWord={selectedWord}
                  onWordClick={handleWordClick}
                  savedWords={savedWords}
                  addedWords={addedWords}
                  isFavorite={isFavorite}
                  onFavoriteClick={toggleFavorite}
                  onPrevSection={sectionIndex > 0 ? handleBack : null}
                  onNextSection={sectionIndex < storySections.length - 1 ? handleNext : null}
                  storySections={storySections}
                />

                {/* ✅ Next button
                {activeTab === "story" && (
                  <div className="story-next-btn-container outside-card">
                      {sectionIndex > 0 && (
                      <button className="story-button-back" onClick={handleBack}>
                        <IoIosArrowBack size={26} />
                      </button>
                    )}
                    <button className="story-button-back" onClick={handleNext}>
                        <IoIosArrowForward size={26} />
                    </button>
                  </div>
                )} */}
              </div>
            )}

              {activeTab === "quiz" && (
                <StoryQuiz
                  storyId={id}
                  story={story}
                  quizUrl={quizUrl}              
                  addedWords={addedWords}
                  onMarkStoryFinished={handleFinishClick}   // 👈 new
                  isFinished={isFinished}                   // 👈 pass current state
                />
              )}
            
              {activeTab === "keywords" && (
                <div className="story-tab-content">
                  <StoryKeywords
                    story={story}
                    onAddFlashcard={handleAddFlashcard}
                    addedWords={addedWords}
                    savedWords={savedWords}
                    onWordClick={handleWordClick}
                  />

                  {/* <div className="story-next-btn-container-flash">
                    <button
                      className="quiz-button"
                      onClick={() => setActiveTab("quiz")}
                    >
                      Próximo →
                    </button>
                  </div> */}
                </div>
              )}

            </div>
          </div>
        </div>

        {selectedWord && (
          <div className={`story-sidebar-box ${isMobile ? "floating" : ""}`} style={isMobile ? floatingStyle : undefined} >
            <StorySidebar
              selectedWord={selectedWord}
              wordData={wordData}
              loadingWord={loadingWord}
              story={story}
              onAddFlashcard={handleAddFlashcard}
              addedWords={addedWords}
              savedWords={savedWords}
            />
          </div>
        )}
      </div>
    </div>
  );
}
